import { createSlice } from '@reduxjs/toolkit';

import authService from 'modules/auth/service';
import dealersApi from '../dealers/service';
import selfServicesApi from '../selfServices/service';
import { formatPhoneNumber } from './utils';
import { CommunicationState } from './types/CommunicationState';
import { WhatsAppStatusEnum } from '../auth/types/LoginRequest';
import { CustomerType } from '../selfServices/types/SelfService';
import { CHANNEL_TO_ADDITIONAL } from './constants';

const initialState: CommunicationState = {};

const communicationSlice = createSlice({
  name: 'communication',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addMatcher(dealersApi.endpoints.getContext.matchFulfilled, (state, action) => {
      const { interventionLastMessageChannel, whatsApp } = action.payload ?? {};
      return {
        ...state,
        interventionLastMessageChannel: interventionLastMessageChannel ?? state.interventionLastMessageChannel,
        whatsApp: { ...(state.whatsApp ?? {}), ...whatsApp },
      };
    });
    builder.addMatcher(authService.endpoints.getMFAConnection.matchFulfilled, (state, action) => {
      const { email,
        additionalCustomerEmail,
        additionalCustomerEnabled,
        additionalCustomerPhoneNumber,
        phoneNumber,
        customerType,
        interventionLastMessageChannel,
        whatsApp,
        smsRetryExpiration,
        emailRetryExpiration,
        whatsAppRetryExpiration } = action.payload ?? {};
      const additionalCustomerInfo = additionalCustomerEnabled ? { additionalCustomerEmail, additionalCustomerPhoneNumber } : {};
      const lastUsedUIChannel = customerType === CustomerType.ADDITIONAL_CUSTOMER && interventionLastMessageChannel
        ? CHANNEL_TO_ADDITIONAL[interventionLastMessageChannel]
        : interventionLastMessageChannel;
      return {
        ...state,
        ...additionalCustomerInfo,
        email,
        phoneNumber,
        customerType,
        lastUsedUIChannel,
        interventionLastMessageChannel,
        whatsApp: { ...(state.whatsApp ?? {}), ...whatsApp },
        smsRetryExpiration,
        emailRetryExpiration,
        whatsAppRetryExpiration,
      };
    });
    builder.addMatcher(authService.endpoints.sendMFACode.matchFulfilled, (state, action) => {
      const { smsRetryExpiration, emailRetryExpiration, whatsAppRetryExpiration } = action.payload ?? {};
      const { channel, customerType } = action.meta.arg.originalArgs;
      const lastUsedUIChannel = customerType === CustomerType.ADDITIONAL_CUSTOMER && channel
        ? CHANNEL_TO_ADDITIONAL[channel]
        : channel;
      return {
        ...state,
        smsRetryExpiration,
        emailRetryExpiration,
        whatsAppRetryExpiration,
        customerType,
        interventionLastMessageChannel: channel ?? state.interventionLastMessageChannel,
        lastUsedUIChannel: lastUsedUIChannel ?? state.lastUsedUIChannel,
      };
    });
    builder.addMatcher(authService.endpoints.login.matchFulfilled, (state) => ({
      ...state,
      smsRetryExpiration: undefined,
      emailRetryExpiration: undefined,
      whatsAppRetryExpiration: undefined,
    }));
    builder.addMatcher(selfServicesApi.endpoints.getSelfServiceById.matchFulfilled, (state, action) => {
      const { email, phoneNumber } = action.payload?.customerInfo ?? {};
      return {
        ...state,
        email,
        phoneNumber: formatPhoneNumber(phoneNumber),
      };
    });
    builder.addMatcher(selfServicesApi.endpoints.updateCustomerInfo.matchFulfilled, (state, action) => {
      const { email, phoneNumber } = action.payload?.customerInfo ?? {};

      const newPhoneNumber = formatPhoneNumber(phoneNumber);
      const hasChangePhoneNumber = state.phoneNumber !== newPhoneNumber;
      const newWhatsAppStatus = hasChangePhoneNumber ? WhatsAppStatusEnum.UNREACHABLE : state.whatsApp?.status;

      return {
        ...state,
        email,
        phoneNumber: newPhoneNumber,
        whatsApp: { ...(state.whatsApp ?? { enabled: false }), status: newWhatsAppStatus },
      };
    });
  },
});

export default communicationSlice;
